import { GAME_PALETTE as C } from "@/lib/game-palette"

/** Paint of a slice: graphite ink, warm ivory, the champagne gold that breaks a same-tone seam, or the stone of "Não foi dessa vez". */
export type WheelToneKind = "ink" | "ivory" | "gold" | "stone"

/** One slice paint: a radial gradient from the hub (`inner`) to the rim (`outer`) and its label color (WCAG AA over both). */
export interface WheelTone {
  inner: string
  outer: string
  text: string
}

/** The fixed palette of the prize games as SVG and canvas paints, drawn from the `GAME_PALETTE` tokens: graphite and ivory slices, a stone miss slice, champagne-gold metal, warm white bulbs and the silver scratch foil; the store logo is its only brand element. */
export const WHEEL_PALETTE = {
  tones: {
    ink: { inner: C["ink-light"], outer: C.ink, text: C.ivory },
    ivory: { inner: C.ivory, outer: C["ivory-deep"], text: C.ink },
    gold: { inner: C["gold-light"], outer: C.gold, text: C.ink },
    stone: { inner: C.stone, outer: C["stone-deep"], text: C.ink },
  } satisfies Record<WheelToneKind, WheelTone>,
  ink: C.ink,
  ivory: C.ivory,
  shade: C.shade,
  gold: { light: C["gold-light"], mid: C.gold, deep: C["gold-deep"] },
  bulb: { core: C.bulb, glow: C["bulb-glow"] },
  foil: { light: C["foil-light"], mid: C.foil, deep: C["foil-deep"] },
  confetti: [C["gold-light"], C.gold, C["gold-deep"], C.ivory, C.ink],
} as const

/** Paint of each slice, clockwise: "Não foi dessa vez" is stone, prizes alternate ink and ivory, and a last prize that would meet the first in ink takes the gold. */
export function wheelToneKinds(misses: readonly boolean[]): WheelToneKind[] {
  const kinds = misses.map((miss, index): WheelToneKind => {
    if (miss) return "stone"
    return index % 2 === 0 ? "ink" : "ivory"
  })
  const last = kinds.length - 1
  if (last > 0 && kinds[last] === "ink" && kinds[0] === "ink") kinds[last] = "gold"
  return kinds
}
